import {useEffect, useMemo, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../hooks/hooks';
import { REmployees } from '../../../features/roles/types';
import { getCosts } from '../../../features/cost-report/slice';
import { 
    Cost, 
    CostReportItem, 
    CreateCostReportItemByCost, 
    CreateEmptyCostReportItemByEmployee 
} from '../../../features/cost-report/types';
import { setCostReportData } from '../../../features/cost-report';
import { PIdNameModel } from '../../../features/projects/types';
import { canSeeOtherCosts } from '../../../features/profile/entityPermissions';

const convertCostsToCostReportItem = (employee: REmployees, projects: PIdNameModel[], costs: Cost[]): CostReportItem[] => {
    if (costs && costs.length > 0) {
        return costs.map((cost) => CreateCostReportItemByCost(employee.id, employee.name, cost, projects));
    }
    
    const temp = CreateEmptyCostReportItemByEmployee(employee.id, employee.name)
    return  [temp];
}

const useCostReportLoader = () =>
{
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const costs = useAppSelector((selector) => selector.costs.items);
    const employees = useAppSelector((selector) => selector.rEmployees);
    const profile = useAppSelector((selector) => selector.profile);
    const dateRangeFilter = useAppSelector((selector) => selector.timeTrackingFilters.filter.dateRangeFilter);
    const propertyFilter = useAppSelector((selector) => selector.timeTrackingFilters.filter.propertyFilter);
    const projects = useAppSelector(store => store.eProjects);
    const canViewOtherCosts = canSeeOtherCosts(profile);
    const ownEmployee = useMemo<REmployees | null>(() => {
        if (!profile.id) {
            return null;
        }

        return employees.find((employee) => employee.id === profile.id)
            ?? { id: profile.id, name: profile.name, isDefault: false };
    }, [employees, profile.id, profile.name]);
    const costReportEmployees = useMemo<REmployees[]>(() => (
        canViewOtherCosts
            ? employees
            : ownEmployee ? [ownEmployee] : []
    ), [canViewOtherCosts, employees, ownEmployee]);
    const selectedEmployeesFilter = propertyFilter.selectedEmployees ?? [];
    const selectedEmployeeIds = useMemo(
        () => {
            if (selectedEmployeesFilter.length > 0) {
                return selectedEmployeesFilter.map((item) => item.originalId as number);
            }

            return [];
        },
        [selectedEmployeesFilter]
    );
    const requestEmployeeIds = useMemo(
        () => {
            if (!canViewOtherCosts) {
                return profile.id ? [profile.id] : [];
            }

            if (selectedEmployeeIds.length > 0) {
                return selectedEmployeeIds;
            }

            return costReportEmployees.map((employee) => employee.id);
        },
        [canViewOtherCosts, costReportEmployees, profile.id, selectedEmployeeIds]
    );
    const reportEmployees = useMemo<REmployees[]>(() => {
        if (!canViewOtherCosts) {
            return costReportEmployees;
        }

        const employeesById = new Map<number, REmployees>();
        costReportEmployees.forEach((employee) => {
            employeesById.set(employee.id, employee);
        });
        costs.forEach((cost) => {
            if (!employeesById.has(cost.employeeId)) {
                employeesById.set(cost.employeeId, {
                    id: cost.employeeId,
                    name: `Employee [${cost.employeeId}]`,
                    isDefault: false,
                });
            }
        });

        return Array.from(employeesById.values());
    }, [canViewOtherCosts, costReportEmployees, costs]);

    const dataFilterEmployeeIds = useMemo(
        () => {
            if (!canViewOtherCosts) {
                return profile.id ? [profile.id] : [];
            }

            return selectedEmployeeIds;
        },
        [canViewOtherCosts, profile.id, selectedEmployeeIds]
    );

    const load = async (
        fromDate: string,
        toDate: string,
        employeeIds: number[]
    ): Promise<Cost[]> => {
        return dispatch(getCosts({
            dateFrom: fromDate,
            dateTo: toDate,
            employees: employeeIds
        })).unwrap();
    };

    useEffect(() => {
        if (!dateRangeFilter?.startDate || !dateRangeFilter?.endDate) {
            return;
        }
        if (!canViewOtherCosts && requestEmployeeIds.length < 1) {
            setCostReportData([]);
            return;
        }

        const loadCosts = async () => {
            setLoading(true);

            try {
                await load(
                    dateRangeFilter.startDate,
                    dateRangeFilter.endDate,
                    requestEmployeeIds
                );
            } finally {
                setLoading(false);
            }
        };

        void loadCosts();
    }, [canViewOtherCosts, dateRangeFilter?.endDate, dateRangeFilter?.startDate, requestEmployeeIds]);

    useEffect(() => {
        const items: CostReportItem[] = reportEmployees.map(employee => {
            const employeeCosts = convertCostsToCostReportItem(employee, projects, costs.filter((cost) => cost.employeeId == employee.id));
            return [...employeeCosts];
        }).flat(1);

        const filteredItems: CostReportItem[] = items
            .filter((x) => (canViewOtherCosts && dataFilterEmployeeIds.length < 1 ? true : dataFilterEmployeeIds.includes(x.employeeId)));

        setCostReportData(filteredItems);

    }, [canViewOtherCosts, costs, dataFilterEmployeeIds, projects, reportEmployees]);

    return { data: useAppSelector((store) => store.costReport.data), loading };
}

export default useCostReportLoader;
